import { Subscription, UNLIMITED_SUBSCRIPTIONS } from '@innovyze/shared-utils';
import {
	Button,
	StylovyzeCard,
	StylovyzeCardFooter,
	useSettings,
	WithLink,
} from '@innovyze/stylovyze';
import React from 'react';

import AccountGroupIcon from '@Components/icons/AccountGroupIcon';
import { useGlobalization } from '@Translations';
import { subscriptionsList } from '@Types/entitlements.types';
import { Product } from '@Types/product.types';
import { addRouteNamespace } from '@Utils/actions';
import getExpiryDateStyleColor from './utils/getExpiryDateStyleColor';
import { DateTime } from 'luxon';
import { momentToLuxonFormat } from '@Utils/formatDateTime';
import { SubscriptionLabel } from './SubscriptionCard.styles';

interface EntitlementCardProps {
	product?: Product;
	hasSecondaryFooter?: boolean;
	height?: number;
	numberOfSubscriptionsUsed: number;
	forceDisableMaxEntitlements?: boolean;
	title?: string;
}

export default function SubscriptionCard({
	product,
	hasSecondaryFooter,
	height,
	numberOfSubscriptionsUsed,
	forceDisableMaxEntitlements,
	title,
}: EntitlementCardProps) {
	const { t } = useGlobalization();
	const { companySettings } = useSettings();
	const entitlementConfig = subscriptionsList(t)?.find(
		el => el?.key === product?.name,
	);

	const entitlementKey = product?.name;

	const displayMaxEntitlements =
		!!forceDisableMaxEntitlements ||
		(product && UNLIMITED_SUBSCRIPTIONS.has(product?.name as Subscription));

	const getQuantity = () => {
		if (displayMaxEntitlements) return numberOfSubscriptionsUsed;
		return `${numberOfSubscriptionsUsed}/${product?.numberOfEntitlements}`;
	};

	const qtyText = getQuantity();

	return (
		<StylovyzeCard
			dataCy="entitlementsCard"
			key={product?.name}
			topColor={entitlementConfig?.color}
			bottomTextLeft={
				<SubscriptionLabel
					color={
						product?.expiry &&
						getExpiryDateStyleColor(product?.expiry)
					}>
					{t('Subscription Renewal')}
					<b>
						{' ' +
							(product?.expiry &&
								DateTime.fromISO(product?.expiry).toFormat(
									momentToLuxonFormat(
										companySettings.dateFormat,
									),
								))}
					</b>
				</SubscriptionLabel>
			}
			countText={qtyText}
			secondaryFooter={
				hasSecondaryFooter && (
					<StylovyzeCardFooter>
						<WithLink
							style={{ width: '100%' }}
							to={addRouteNamespace(`${entitlementKey}`)}>
							<Button
								color="primary"
								startIcon={<AccountGroupIcon color="#007CA0" />}
								dataCy="manageUserButton"
								fullWidth>
								{t('Manage Users')}
							</Button>
						</WithLink>
					</StylovyzeCardFooter>
				)
			}
			topSubText={t('Used')}
			topText={title || entitlementConfig?.value}
			height={height}
		/>
	);
}
